import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import Modal from './shared/Modal';
import Icon from './shared/Icon';
import Spinner from './shared/Spinner';
import TripService from '../services/tripService';
import { resizeForSurface } from '../utils/imageSource';

/**
 * "Inspired by … & N others" → full source list. THE discovery surface: each
 * row links to a source trip (traffic back to the creator who inspired this
 * one). Privacy-gated per row — viewable sources link through; gated sources
 * show the creator handle but no link, with a friend-gate tooltip.
 *
 * Sources can be passed in (`sources` prop — the detail page already has the
 * full provenance from getTripById) OR fetched by tripId (the feed card only
 * carries a summary, so it fetches on open via /api/trips/:id/provenance).
 *
 * Props:
 *   isOpen, onClose
 *   tripId:  fetch sources for this trip when no `sources` prop given
 *   sources: optional pre-loaded array [{ trip_id, trip_name, days_copied,
 *            creator_handle, creator_name, creator_avatar,
 *            creator_profile_picture, viewable }]
 */
function ProvenanceSourcesModal({ isOpen, onClose, tripId, sources: sourcesProp }) {
  const { t } = useTranslation('modals');
  const [sources, setSources] = useState(sourcesProp || []);
  const [loading, setLoading] = useState(!sourcesProp);

  useEffect(() => {
    if (!isOpen) return;
    if (sourcesProp) { setSources(sourcesProp); setLoading(false); return; }
    if (!tripId) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      const result = await TripService.getTripProvenance(tripId);
      if (cancelled) return;
      setSources(result.success ? (result.provenance?.sources || []) : []);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [isOpen, tripId, sourcesProp]);

  const renderAvatar = (s) => {
    if (s.creator_profile_picture) {
      return (
        <img
          src={resizeForSurface(s.creator_profile_picture, 'thumb')}
          alt={s.creator_name || t('provenanceSources.creatorFallback')}
          className="w-9 h-9 rounded-full object-cover flex-shrink-0"
        />
      );
    }
    return (
      <div className="w-9 h-9 rounded-full bg-primary-lighter flex items-center justify-center flex-shrink-0">
        <span className="text-primary-dark text-sm">{s.creator_avatar || '👤'}</span>
      </div>
    );
  };

  // The creator's display label: @handle if claimed, else their name.
  const creatorLabel = (s) => (s.creator_handle ? `@${s.creator_handle}` : (s.creator_name || t('provenanceSources.creatorFallback')));

  const renderRow = (s) => {
    const label = creatorLabel(s);
    const daysText = t('provenanceSources.daysCopied', { count: s.days_copied });

    const inner = (
      <>
        {renderAvatar(s)}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <span className="text-sm font-semibold text-content-primary truncate">{label}</span>
            {!s.viewable && <Icon name="lock" className="w-3 h-3 text-content-muted flex-shrink-0" />}
          </div>
          <div className="text-xs text-content-secondary truncate">
            {s.viewable ? s.trip_name : t('provenanceSources.privateTripFallback')} · {daysText}
          </div>
        </div>
        {s.viewable && <Icon name="chevronRight" className="w-4 h-4 text-content-muted flex-shrink-0" />}
      </>
    );

    if (s.viewable) {
      return (
        <Link
          key={s.trip_id}
          to={`/shared/trip/${s.trip_id}`}
          onClick={onClose}
          className="flex items-center gap-3 p-2.5 rounded-lg hover:bg-surface-secondary transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-light"
        >
          {inner}
        </Link>
      );
    }
    // Gated: not a link. Tooltip explains why (the Sarah/Tom/Ryan rule).
    return (
      <div
        key={s.trip_id}
        className="flex items-center gap-3 p-2.5 rounded-lg opacity-75 cursor-default"
        title={t('provenanceSources.gatedTooltip', { name: label })}
      >
        {inner}
      </div>
    );
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md" ariaLabel={t('provenanceSources.header.ariaLabel')}>
      <Modal.Header onClose={onClose}>
        <h2 className="font-display font-medium text-xl text-content-primary">
          {t('provenanceSources.header.title')}
        </h2>
      </Modal.Header>
      <Modal.Body>
        {loading ? (
          <div className="flex justify-center py-8"><Spinner /></div>
        ) : sources.length === 0 ? (
          <p className="text-sm text-content-muted py-4 text-center">{t('provenanceSources.emptyState')}</p>
        ) : (
          <div className="space-y-1">
            {sources.map(renderRow)}
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
}

export default ProvenanceSourcesModal;
